import React, { useState, useEffect, useContext } from "react";
import { LDialog, LComboBox, LFade } from "@loffa/gpp-modules";
import styles from "../../TemplateManagement.module.css"
import { AuthContext } from "../../../../../../../context/AuthContext";
import KeyBasedLButton from "../../../../../../Shared/KeyBasedLButton";

//using the same modal to handle updating a users template map & duplicating a template map since UI is identical
const EditTemplateMapModal = ({isOpen, initialData, onClose, onRefetch, clients, loffaTemplates, mode = "update"}) => {
  const [loading, setLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedTemplateName, setSelectedTemplateName] = useState("");
  const [error, setError] = useState(null);
  const authContext = useContext(AuthContext);
  const token = authContext.authState().accessToken.accessToken
  //filter through active clients + products to find data associated with the template map - needed because this data is not a part of the data manager api ms response
  useEffect(() => {
    if (isOpen && initialData) {
      const currentClient = clients.find((c) => c.clientValuePayload?.clientGuid === initialData.clientId) || null;
      setSelectedClient(currentClient);

      const currentTemplate = loffaTemplates.find((t) => t?.templateId === initialData.loffaTemplateId) || null;
      setSelectedTemplate(currentTemplate);

      setSelectedTemplateName(mode === "update" ? initialData.name : initialData.name + " (copy)");
    }
  }, [isOpen, initialData, clients, loffaTemplates]);

  //handleSave will send either a POST req or a PUT req depending on if they are updating a map or duplicating one
  const handleSave = async (e) => {
    e.preventDefault();

    if (!selectedClient || !selectedTemplate || !selectedTemplateName) {
      setError("Please fill in all required fields");
      return;
    }
    setLoading(true);
    setError(null);
    const payload = {
      name: selectedTemplateName,
      clientId: selectedClient.clientValuePayload.clientGuid,
      loffaTemplateId: selectedTemplate.templateId,
      productType: selectedTemplate.productType,
      clientProductId: initialData.clientProductId,
      templateMapId: initialData.templateMapId
    };

    try {
      let response;
      if (mode === "update") {
        response = await fetch(`${process.env.REACT_APP_API_GATEWAY_BASE}/datamanager/api/Template/TemplateMap/${initialData.templateMapId}/`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
          }
        );
      } else if (mode === "duplicate") { //creates a new entry if duplicated
        response = await fetch(`${process.env.REACT_APP_API_GATEWAY_BASE}/datamanager/api/Template/TemplateMap/Duplicate`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
          }
        );
      }

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          errorText ||
            `Failed to ${mode === "update" ? "update" : "duplicate"} template map`
        );
      }
      await onRefetch();
      onClose();
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClientSelect = (e) => {
    setSelectedClient(e.target.value);
  };

  const handleTemplateSelect = (e) => {
    setSelectedTemplate(e.target.value);
  };

  const modalBody =  (
  <div className={styles.modalBodyContent}>
    {error && <div className={styles.templateManagerError}>{error}</div>}
    <div className={styles.formGroup}>
      <label>Name</label>
      <input
        type="text"
        value={selectedTemplateName || ""}
        onChange={(e) => setSelectedTemplateName(e.target.value)}
        className={styles.formInput}
      />
    </div>

    <div className={styles.formGroup}>
      <label>Loffa Template</label>
      <LComboBox
        data={loffaTemplates}
        textField="name"
        valueField="templateId"
        dataItemKey="templateId"
        value={selectedTemplate}
        onChange={handleTemplateSelect}
        style={{ width: "100%" }}
      />
    </div>

    <div className={styles.formGroup}>
      <label>Client</label>
      <LComboBox
        data={clients}
        textField="formattedName"
        valueField="clientValuePayload"
        value={selectedClient}
        onChange={handleClientSelect}
        style={{ width: "100%" }}
      />
    </div>
  </div>
  )

  return (
    <LFade transitionEnterDuration={300}>
      {isOpen && (
        <LDialog
          width="500"
          title={
            mode === "update"
              ? "Update Client Template Map"
              : "Duplicate Client Template Map"
          }
          body={modalBody}
          actionLayout="stretched"
          action={
            selectedClient && selectedTemplate && selectedTemplateName ? (
              <KeyBasedLButton
                parentKey={`${selectedClient?.clientValuePayload?.clientGuid}-${selectedTemplate?.templateId}-${selectedTemplateName}`}
                buttonText="Save"
                themeColor="primary"
                className={`green ${styles.stretchButton}`}
                onClick={handleSave}
              />
            ) : null
          }
          actionCloseButton={onClose}
          actionOverlay={onClose}
        />
      )}
    </LFade>
  );
};

export default EditTemplateMapModal;
